import React, {
  useRef,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import SearchPanel, { ValueChangedEvent } from "@widgets/SearchPanel"; // 조회조건 Panel
import { callApi, fnGetDataSet, fnSave } from "@shared/api/restApi"; // BackEnd API
import { getInitFormData, getAutoSlip } from "@shared/utils/CommonUtils"; // 공통 유틸 메뉴 초기화(조회조건, 그리드, 프리폼) 설정 load 관련 내용
import { showDialog } from "@widgets/CommonDialog"; // Message Box
import { global } from "@shared/libs/Global"; // Login User관련 전역 변수( 로그인 정보)
import SnGrid from "@shared/components/Grid/SnGrid"; // 공통 그리드
import { menuInfo } from "@shared/type/CommonType"; // 공통 Interface
import SnButton from "@shared/components/SnButton";

interface Props {
  Menuprops: menuInfo;
}

interface RowData {
  KeyField?: string; // 행의 고유 ID
  [key: string]: any;
}

const SN_MTL_EVL_REG_001 = forwardRef(({ Menuprops }: Props, ref) => {
  //#region  variable
  const gridApiRefH = useRef<any>(null); // 첫 번째  그리드 Reference 변수
  const searchRef = useRef<any>(null);
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const today = new Date();
  const todayform = `${today.getFullYear()}${(today.getMonth() + 1)
    .toString()
    .padStart(2, "0")}${today.getDate().toString().padStart(2, "0")}`;
  const { userInfo } = global(); // Login user 정보
  //#endregion

  //#region  Components useState
  const [formData, setFormData] = useState<any>(); // 조회조건 변수
  const [formControlData, setFormControlData] = useState<any[]>([]); // 조회조건 설정된 항목 binding
  const [columns1, setColums1] = useState<any[]>([]); // gridApiRefH 컬럼 설정 관련
  const [dataSource1, setDataSource1] = useState<any[]>([]); // gridApiRefH datasource 관련

  //#endregion

  //#region grid 높이 관련
  let height1 = "49.5%";
  const menuSize = localStorage.getItem("menuSize");
  if (menuSize) {
    const menu = JSON.parse(menuSize);
    height1 = menu.SN_MTL_EVL_REG_001?.grid1Height
      ? menu.SN_MTL_EVL_REG_001?.grid1Height
      : height1;
  }

  //#endregion

  useEffect(() => {
    const fetchform = async () => {
      setDataSource1([]);
      try {
        const rtn: any = await getInitFormData();

        setFormControlData(rtn.formControlData); // 조회조건 항목 설정
        setFormData(rtn.formData); // 조회조건 초기값
        setColums1(rtn.grid.grid1); // H그리드 항목 설정
      } catch (error) {}
    };
    fetchform();
    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    };
  }, []);

  //#region  Button Event
  useImperativeHandle(ref, () => ({
    onSearch,
    onInsert,
    onDelete,
    onSave,
  }));

  const onSearch = async () => {
    const modifiedRowsH = gridApiRefH.current.getChange();
    if (modifiedRowsH.modifiedRowcount > 0) {
      const confirm = await showDialog(
        "confirm",
        "수정된 내용이 있습니다. 저장하시겠습니까?"
      );
      if (confirm) {
        await onSave();
      }
    }
    try {
      const params = {
        id: "sn_mtl_evl_reg_001_01",
        params: searchRef.current.getFormdata(),
      };
      setDataSource1([]);
      const data: any = await fnGetDataSet(params);
      setDataSource1(data[0]);

      if (data[0] && data[0].length > 0) {
        // 1. 그리드 데이터가 바인딩되었음을 기다림
        await new Promise((resolve) => setTimeout(resolve, 50)); // 비동기 반영 대기 (최적화 가능)

        // 2. 첫 번째 행 선택 및 포커스 설정
        gridApiRefH.current.selectAndFocusRow(0);
      }

      gridApiRefH.current.resetUpdate();
    } catch (error: any) {}
  };

  const onInsert = async () => {
    try {
      const newRow = {}; // 빈 객체
      gridApiRefH.current?.addRow(newRow);
    } catch (error: any) {}
  };

  const onDelete = async () => {
    const gridApiH = gridApiRefH.current.api;
    const rowCount = gridApiH.getDisplayedRowCount();
    if (rowCount === 0) {
      return;
    }
    const confirm = await showDialog("confirm", "삭제하시겠습니까?");

    if (!confirm) {
      return; // 사용자가 아니오를 선택하면 함수 종료
    }

    gridApiRefH.current?.deleteSelectedRows();

    const realDeletedRows = gridApiRefH.current.getChange().delete ?? [];
    if (Array.isArray(realDeletedRows) && realDeletedRows.length < 1) {
      // 실제로 삭제된 행이 없으면 변경 사항을 적용하고 반환합니다.
      gridApiRefH.current.resetUpdate();
      return;
    }

    await onSave();
  };

  //initialize new row(set default value)
  const onBeforeAddRowH = (newRow: RowData): boolean => {
    try {
      const mtlisureqhChanges = gridApiRefH.current.getChange().insert;
      const searchFormData = searchRef.current.getFormdata();
      if (mtlisureqhChanges.length > 0) return false;

      const updatedRow = {
        cd_firm: userInfo.cd_firm,
        cd_biz: searchFormData.cd_biz,
      };

      Object.assign(newRow, updatedRow); // 원본 객체 수정

      return true;
    } catch (error: any) {
      return false; // 추가 취소
    }
  };

  const onSave = async () => {
    let mtlIsureqhChanges;
    try {
      gridApiRefH.current.api.stopEditing();
      // H 필수값 체크
      const requiredValuesCheckH = gridApiRefH.current.requiredValuesCheck();
      if (!requiredValuesCheckH) {
        return;
      }

      mtlIsureqhChanges = gridApiRefH.current.getChange();
      const headerInserts = mtlIsureqhChanges.insert ?? [];

      if (mtlIsureqhChanges.modifiedRowcount === 0) {
        showDialog("alter", "변경된 내용이 없습니다.");
        return;
      }

      //신규 등록된 수주건이 있는 경우 채번
      for (let row of headerInserts) {
        if (!row.no_req) {
          const cd_module = "MTL";
          try {
            // getNoSlip 호출
            const no_slip = await getAutoSlip(
              cd_module,
              10, // no_line 값이 필요한 경우 사용
              row.dt_req, // 해당 날짜를 넘겨줘야 할 경우
              Menuprops.cd_menu || "SN_MTL_EVL_REG_001"
            );
            if (no_slip) {
              row.no_req = no_slip; // no_req 값을 추가

              const gridApiH = gridApiRefH.current.api;
              const focusedCell = gridApiH.getFocusedCell();
              if (focusedCell) {
                const rowNode = gridApiH.getRowNode(focusedCell.rowIndex);
                rowNode.setDataValue("no_req", no_slip);
              }
            }
          } catch (error: any) {}
        }
      }
      // 요청 데이터 구성

      const SaveData = {
        mtl_isureqh: mtlIsureqhChanges,
      };
      // 저장 API 호출
      await fnSave("SN_MTL_EVL_REG_001_SAVE_01", SaveData);

      // 성공적으로 저장된 후 초기화
      gridApiRefH.current.resetUpdate();
    } catch (error: any) {}
  };
  //#endregion

  const onCellValueChangedH = async (event: any) => {
    if (!event) return;
  };

  const onSearchValueChanged = (event: ValueChangedEvent) => {
    if (event.source !== "popup") return;
    if (event.colId !== "dt_from" && event.colId !== "dt_to") return;

    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      searchTimerRef.current = null;
      onSearch();
    }, 0);
  };

  const onGrid1BtnsClick = async (id: string) => {
    if (id == "btnEvaluation") {
      try {
        // parameter 세팅
        const formData = searchRef.current.getFormdata();
        const dt_from = formData.dt_from.substring(0, 6);
        const dt_to = formData.dt_to.substring(0, 6);
        const prevMonth = dt_from.substring(4, 6);
        let tmp = parseInt(prevMonth);
        if (tmp - 1 == 0) tmp = 0;
        else tmp = tmp - 1;
        const prevM =
          dt_from.substring(0, 4) +
          (tmp.toString().length > 1 ? tmp.toString() : "0" + tmp.toString());
        const yy = dt_to.substring(0, 4);
        let sdt_cls = dt_to + "01";
        // DaysInMonth 로직
        const year = sdt_cls.substring(0, 4);
        const month = sdt_cls.substring(4, 6);
        const day = sdt_cls.substring(6, 8);
        const dt_cls = new Date(`${year}-${month}-${day}T00:00:00`);
        const lastDay = new Date(
          dt_cls.getFullYear(),
          dt_cls.getMonth() + 1,
          0
        ).getDate();
        sdt_cls = dt_to + lastDay.toString();

        // api 호출
        const params = {
          cd_firm: userInfo.cd_firm,
          dt_from: dt_from,
          dt_to: dt_to,
          prevM: prevM,
          cd_biz: formData.cd_biz,
          cd_emp: userInfo.cd_emp,
          yy: yy,
          sdt_cls: sdt_cls,
          cd_dept: userInfo.cd_dept,
        };
        const result = await callApi(
          "SN_MTL_EVL_REG_001_btnEvaluation",
          params
        );

        // 결과 처리
        if (result.status) {
          onSearch();
        }
      } catch (error: any) {}
    }
  };

  //#endregion

  //#region  UI
  return (
    <div className="h-full flex flex-col">
      <div className="mb-1">
        <SearchPanel
          ref={searchRef}
          data={formData}
          controls={formControlData}
          onValueChanged={onSearchValueChanged}
        ></SearchPanel>
      </div>
      <div className="flex-1">
        <SnGrid
          // Pros로 받은 Menuprops
          seq={1} // Grid Seqno 우클릭 메뉴용
          ref={gridApiRefH} // Grid Name
          columnConfig={columns1} // 컬럼 설정 전달
          rowData={dataSource1} // Datasource
          isEditable={true} // 수정 가능여부
          rowSelection="single" // 로우 선택 모드 (단일)
          onBeforeAddRow={onBeforeAddRowH} // 행추가 전 유효성 체크 및 초기값 설정
          onCellValueChanged={onCellValueChangedH}
          title={"재고평가정보"}
          header={
            <div>
              <SnButton
                text={"재고평가"}
                onClick={() => {
                  onGrid1BtnsClick("btnEvaluation");
                }}
              />
            </div>
          }
        />
      </div>
    </div>
  );
  //#endregion
});

export default SN_MTL_EVL_REG_001;
